import React from 'react';
import { useTranslation } from 'react-i18next';
import { Tooltip } from '../../../ui/Tooltip/Tooltip';
import { BUTTON_BASE_CHROME, SIZE_CLASSES } from '../../../ui/Button/Button';
import { TranslationKey } from '../../../i18n/translationKeys';
import { EmailTone } from '../../../types/customerFollowUp/enums';
import { CUSTOM_TONE } from '../../../types/customEmailTemplate';
import type { ToneChoice } from '../../../types/customEmailTemplate';

interface ToneSelectorProps {
    value: ToneChoice;
    onChange: (value: ToneChoice) => void;
    disabled?: boolean;
    className?: string;
    /** Adds the chip for the company's custom template. */
    showCustom?: boolean;
}

/** A selected tone reads as the strongest border/text pair on the ramp, an unselected one as the
 * faintest — intensity carries the state instead of a fill, since every option stays on the same
 * bordered chip. */
const toneButtonClassName = (selected: boolean, disabled: boolean): string => {
    const base =
        `${SIZE_CLASSES.sm} ${BUTTON_BASE_CHROME} text-compact inline-flex items-center justify-center font-semibold focus-visible:border`;

    // A locked form must still show which tone the draft is written in.
    if (disabled) {
        const lockedColors = selected ? 'text-content-subtle border-primary-900' : 'text-content-tertiary border-line-subtle';
        return `${base} bg-primary-50 dark:bg-primary-100 border ${lockedColors} cursor-not-allowed`;
    }

    const colors = selected ? 'border-primary-900 text-content' : 'border-primary-400 text-content-subtle';

    return `${base} bg-primary-50 hover:bg-primary-200 active:bg-primary-300 ` +
        `dark:bg-primary-100 dark:hover:bg-primary-400 dark:active:bg-primary-500 border ${colors} cursor-pointer`;
};

export const ToneSelector: React.FC<ToneSelectorProps> = ({ value, onChange, disabled = false, className = '', showCustom = false }) => {
    const { t } = useTranslation();

    const options: { value: ToneChoice; label: string; description: string; 'data-testid': string }[] = [
        {
            value: EmailTone.COURTEOUS,
            label: t(TranslationKey.CustomerFollowUp.Email.Tones.Courteous),
            description: t(TranslationKey.CustomerFollowUp.Email.ToneDescriptions.Courteous),
            'data-testid': `email-tone-${EmailTone.COURTEOUS}`,
        },
        {
            value: EmailTone.PROFESSIONAL,
            label: t(TranslationKey.CustomerFollowUp.Email.Tones.Professional),
            description: t(TranslationKey.CustomerFollowUp.Email.ToneDescriptions.Professional),
            'data-testid': `email-tone-${EmailTone.PROFESSIONAL}`,
        },
        {
            value: EmailTone.SEVERE,
            label: t(TranslationKey.CustomerFollowUp.Email.Tones.Severe),
            description: t(TranslationKey.CustomerFollowUp.Email.ToneDescriptions.Severe),
            'data-testid': `email-tone-${EmailTone.SEVERE}`,
        },
        ...(showCustom ? [{
            value: CUSTOM_TONE,
            label: t(TranslationKey.CustomerFollowUp.Email.Tones.Custom),
            description: t(TranslationKey.CustomerFollowUp.Email.ToneDescriptions.Custom),
            'data-testid': `email-tone-${CUSTOM_TONE}`,
        }] : []),
    ];

    return (
        <div role="group" aria-label={t(TranslationKey.CustomerFollowUp.Email.Tone)} className={`flex flex-wrap gap-s ${className}`}>
            {options.map((option) => (
                <Tooltip key={option.value} content={option.description}>
                    {(describedById) => (
                        <button
                            type="button"
                            className={toneButtonClassName(value === option.value, disabled)}
                            aria-pressed={value === option.value}
                            aria-describedby={describedById}
                            disabled={disabled}
                            onClick={() => onChange(option.value)}
                            data-testid={option['data-testid']}
                        >
                            {option.label}
                        </button>
                    )}
                </Tooltip>
            ))}
        </div>
    );
};
